import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/shared/components/ui/dialog";
import { Button } from "@/shared/components/ui/button";
import { Input } from "@/shared/components/ui/input";
import { Label } from "@/shared/components/ui/label";
import { useRetireDocument } from "./use-documents";
import type { TermsOfService } from "../shared/compliance-types";
import { resolveComplianceError } from "../shared/resolve-compliance-error";
import { documentTypeLabel } from "../shared/document-type-label";

interface Props {
  readonly open: boolean;
  readonly onOpenChange: (open: boolean) => void;
  readonly doc: TermsOfService;
}

/**
 * Convert an `<input type="datetime-local">` value (no tz suffix) into
 * an ISO UTC string the backend accepts. The browser presents the value
 * in local time; we treat it as wall-clock in the user's timezone, which
 * is also what the operator visually selects.
 */
function localInputToIso(value: string): string | null {
  if (!value) return null;
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return null;
  return parsed.toISOString();
}

/** Format a Date for use as the default value of datetime-local (local wall-clock). */
function nowAsLocalInput(): string {
  const now = new Date();
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}T${pad(now.getHours())}:${pad(now.getMinutes())}`;
}

export function RetireDocumentDialog({ open, onOpenChange, doc }: Props) {
  const { t } = useTranslation();
  const retire = useRetireDocument();
  const [effectiveUntil, setEffectiveUntil] = useState(nowAsLocalInput());
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (open) {
      // Pre-fill with the existing scheduled date when re-scheduling,
      // otherwise default to now (= retire immediately).
      if (doc.state === "SCHEDULED_RETIRE" && doc.effectiveUntil) {
        const d = new Date(doc.effectiveUntil);
        const pad = (n: number) => String(n).padStart(2, "0");
        setEffectiveUntil(
          `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`,
        );
      } else {
        setEffectiveUntil(nowAsLocalInput());
      }
      setError(null);
    }
  }, [open, doc.state, doc.effectiveUntil]);

  const iso = localInputToIso(effectiveUntil);
  // Client-side parity with backend's "must be present or future" rule.
  // Subtract a 1-minute grace so a value the operator just picked at
  // wall-clock "now" doesn't flip invalid by the time they click submit.
  const isFuture = iso != null && new Date(iso).getTime() >= Date.now() - 60_000;
  const isValid = isFuture;

  const isImmediate = iso != null && new Date(iso).getTime() <= Date.now() + 60_000;

  const handleSubmit = async () => {
    if (!isValid || !iso || retire.isPending) return;
    setError(null);
    try {
      await retire.mutateAsync({ id: doc.id, input: { effectiveUntil: iso } });
      onOpenChange(false);
    } catch (err) {
      setError(resolveComplianceError(err, t));
    }
  };

  return (
    <Dialog open={open} onOpenChange={(next) => !retire.isPending && onOpenChange(next)}>
      <DialogContent data-testid="compliance-retire-document-dialog">
        <DialogHeader>
          <DialogTitle>{t("compliance.documents.retire.title")}</DialogTitle>
          <DialogDescription>
            {t("compliance.documents.retire.description", {
              role: t(`compliance.roles.${doc.roleScope}`),
              type: documentTypeLabel(doc),
              version: doc.version,
            })}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="retire-effective-until">
              {t("compliance.documents.retire.dateLabel")}
            </Label>
            <Input
              id="retire-effective-until"
              type="datetime-local"
              data-testid="compliance-retire-effective-until-input"
              value={effectiveUntil}
              onChange={(e) => setEffectiveUntil(e.target.value)}
              min={nowAsLocalInput()}
            />
            <p className="text-xs text-muted-foreground">
              {t("compliance.documents.retire.dateHint")}
            </p>
          </div>

          {!isFuture && (
            <p data-testid="compliance-retire-past-date-error" className="text-sm text-destructive">
              {t("compliance.documents.retire.pastDateError")}
            </p>
          )}

          <div className="rounded-md border border-wander-orange/30 bg-wander-orange/5 p-3 text-sm">
            <p className="font-medium">{t("compliance.documents.retire.warningHeading")}</p>
            <p className="text-muted-foreground">
              {isImmediate
                ? t("compliance.documents.retire.warningImmediate")
                : t("compliance.documents.retire.warningScheduled")}
            </p>
          </div>

          {error && (
            <p data-testid="compliance-retire-error" className="text-sm text-destructive">
              {error}
            </p>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={retire.isPending}>
            {t("compliance.actions.cancel")}
          </Button>
          <Button
            variant="destructive"
            data-testid="compliance-retire-document-submit"
            onClick={handleSubmit}
            disabled={!isValid || retire.isPending}
          >
            {retire.isPending
              ? t("compliance.documents.retire.retiring")
              : isImmediate
                ? t("compliance.documents.retire.confirmImmediate")
                : t("compliance.documents.retire.confirmScheduled")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
